import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";

/**
 * The tier-1 write confirmation: a question, the consequences, and two buttons.
 *
 * Tier-1 is everything that creates or re-queues rather than destroys — a
 * callback replay, clearing a config. There is deliberately no typed
 * confirmation here; that is tier-2's shape (PRO-1035), and asking someone to
 * type a merchant's name before re-sending a webhook trains them to type it
 * without reading, which is exactly the habit tier-2 needs them not to have.
 *
 * What this is *not* is an authorization boundary. Every staff member can do
 * everything on this surface and the audit log is the only real control; this is
 * a speed bump that makes someone read one sentence before they act.
 *
 * `body` carries the consequences. A confirm dialog whose body restates the
 * button label ("Replay this delivery?" / "Replay") is a click-through, so
 * callers are expected to say what will actually happen — which endpoint gets
 * called, how many rows get cancelled, what has changed since.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  body,
  confirmLabel,
  cancelLabel = "Cancel",
  destructive = false,
  pending = false,
  confirmDisabled = false,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Consequences, warnings, counts. Rendered above the buttons. */
  body?: React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  /** Colours the confirm button. Use it when the action removes something. */
  destructive?: boolean;
  /** Disables both buttons and shows the pending label while the write runs. */
  pending?: boolean;
  /** Keeps confirm disabled while a required field in `body` is empty. */
  confirmDisabled?: boolean;
  onConfirm: () => void;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // A write already in flight cannot be called back, so closing the dialog
        // mid-request would only hide it. Let it finish and report.
        if (pending && !next) return;
        onOpenChange(next);
      }}
      // The confirm and cancel buttons are the only two ways out. A stray click
      // on the backdrop should not read as either answer.
      disablePointerDismissal
    >
      <DialogContent showClose={!pending}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>

        {body ? <div className="space-y-3 text-sm">{body}</div> : null}

        <DialogFooter>
          <Button
            variant="ghost"
            size="sm"
            disabled={pending}
            onClick={() => onOpenChange(false)}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? "destructive" : "default"}
            size="sm"
            disabled={pending || confirmDisabled}
            onClick={onConfirm}
          >
            {pending ? "Working…" : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
